import { ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { environment } from '../../environments/environment';

import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';

const uri = environment.apiUrl;
const wssUri = environment.wssUrl;
var connectionParams = {
  authorization: '',
};

function createApollo(httpLink: HttpLink) {
  const middleware = new ApolloLink((request, forward) => {
    // Request-level override: SuperAdmin-only operations can opt in to be
    // executed under the original (pre-impersonation) identity by setting
    // `useOriginalAuth: true` in the operation context.
    const ctx = request.getContext() as any;
    const useOriginalAuth = ctx?.useOriginalAuth === true;

    let token: string | null = null;

    if (useOriginalAuth) {
      const original = JSON.parse(localStorage.getItem('originalCurrentUser') || 'null');
      token = original?.authToken || original?.token || null;
    }

    if (!token) {
      const storedUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      token = storedUser?.authToken || storedUser?.token || localStorage.getItem('token');
    }

    if (token) {
      request.setContext({
        headers: {
          ...(ctx?.headers || {}),
          authorization: `Bearer ${token}`,
        },
      });
    }

    connectionParams.authorization = token ? `Bearer ${token}` : '';
    return forward(request);
  });

  let httpsLink = middleware.concat(httpLink.create({ uri }));

  // Wss link
  // TODO new protocol
  const wsLink = new WebSocketLink({
    uri: wssUri,
    options: {
      lazy: true,
      reconnect: true,
      reconnectionAttempts: 100,
      connectionParams,
    },
  });

  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpsLink,
  );

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          staffs: {
            keyArgs: ['pageRequest', ['limit']],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          resources: {
            keyArgs: ['pageRequest', ['limit']],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          categorys: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          services: {
            keyArgs: ['pageRequest', ['limit']],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          customers: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          rota: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          calendar: {
            keyArgs: ['staffId'],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          appointments: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          staffByService: {
            keyArgs: ['serviceId'],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          freeSlots: {
            keyArgs: ['accountId', 'date', 'serviceId', 'staffId'],
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          translations: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          invoices: {
            keyArgs: false,
            merge(existing = [], incoming) {
              return incoming;
            },
          },
          superAdminDashboard: {
            keyArgs: false,
            merge(existing, incoming) {
              return incoming;
            },
          },
          superAdminAccountList: {
            keyArgs: false,
            merge(existing, incoming) {
              return incoming;
            },
          },
          accountDashboard: {
            keyArgs: ['accountId'],
            merge(existing, incoming) {
              return incoming;
            },
          },
          systemNotifications: {
            keyArgs: false,
            merge(existing, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  });

  //Enable apollo extension for debug in dev and staging environment
  return {
    link,
    cache,
    clientAwareness: {
      name: 'Buky',
      version: '1.0.2'
    },
    devtools: {
      enabled: !environment.production
    }
  };
}

export const provideApollo = () => [
  {
    provide: APOLLO_OPTIONS,
    useFactory: createApollo,
    deps: [HttpLink],
  },
  Apollo,
  HttpLink,
];
